import { Box } from '@chakra-ui/react';
import type { MouseEvent } from 'react';
import type { SingleValue } from 'react-select';

import {
  type SecondaryDimensionValues,
  secondaryDimensionOptions,
} from '../../PageConfigProvider/types/dimension';
import { usePageState } from '../../PageState/PageStateProvider';
import { useChart } from '../ChartProvider';
import { StyledPlotSelect } from '../StyledPlotSelect';

type Option = {
  value: SecondaryDimensionValues;
  label: string;
};

export const SecondaryDimensionSwitcher = () => {
  const { item } = useChart();
  const { id, plot } = item ?? {};
  const { pageState, dispatch: pageDispatch } = usePageState();

  const defaultDimensionSource = plot?.dimensionSource;
  const alternateDimensionSource = plot?.alternateDimensionSource;

  if (
    !defaultDimensionSource ||
    defaultDimensionSource === 'primary' ||
    !alternateDimensionSource
  ) {
    return null;
  }

  const options = [
    {
      value: defaultDimensionSource,
      label: getSegmentLabel(defaultDimensionSource),
    },
    ...(alternateDimensionSource
      .filter(
        (source): source is SecondaryDimensionValues => source !== 'primary'
      )
      ?.map((source) => ({
        value: source,
        label: getSegmentLabel(source),
      })) ?? []),
  ];

  const selectedDimensionSource =
    pageState.dimensionSourceOverrides?.[item.id] ?? defaultDimensionSource;

  const selectedSegment =
    selectedDimensionSource && selectedDimensionSource !== 'primary'
      ? {
          value: selectedDimensionSource,
          label: getSegmentLabel(selectedDimensionSource),
        }
      : null;

  const handleChange = (newValue: SingleValue<Option>) => {
    if (!newValue) return;

    pageDispatch({
      type: 'SET_DIMENSION_SOURCE_OVERRIDE',
      payload: {
        plotId: id,
        dimensionSource: newValue.value,
      },
    });
  };

  if (!options || options?.length <= 1) {
    return null;
  }

  return (
    <Box width="100%" onClick={(e: MouseEvent) => e.stopPropagation()}>
      <StyledPlotSelect
        value={selectedSegment}
        onChange={handleChange}
        options={options}
        height="24px"
        isOptionSelected={(option) => option.value === selectedSegment?.value}
        isSearchable
        maxWidth={140}
      />
    </Box>
  );
};

const getSegmentLabel = (segment: SecondaryDimensionValues) =>
  secondaryDimensionOptions.find((option) => option.value === segment)?.label ??
  segment;
